import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { getTodayDateString } from '@/lib/utils';
import { useCreateJobCharge } from '../hooks/useCreateJobCharge';
import { useUpdateJobCharge } from '../hooks/useUpdateJobCharge';
import { useJobEntryDuplicateCheck } from '../hooks/useJobEntryDuplicateCheck';
import { uploadChargeImages } from '../api/chargeImages';
import { ChargeImagesSection } from './ChargeImagesSection';
import { DuplicateEntryWarningDialog } from './DuplicateEntryWarningDialog';
import type { JobCharge } from '../types/jobCharge';
import type { PotentialJobEntryDuplicate } from '../api/checkJobEntryDuplicates';

interface ChargeEntryFormProps {
  jobId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCharge?: JobCharge | null;
  isForecast: boolean;
}

export function ChargeEntryForm({
  jobId,
  isOpen,
  onClose,
  onSuccess,
  editingCharge,
  isForecast,
}: ChargeEntryFormProps) {
  const { create, isCreating, error: createError } = useCreateJobCharge();
  const { update, isUpdating, error: updateError } = useUpdateJobCharge();
  const { checkForChargeDuplicates, isChecking } = useJobEntryDuplicateCheck();

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<PotentialJobEntryDuplicate[]>([]);
  const [duplicatesAcknowledged, setDuplicatesAcknowledged] = useState(false);

  // Form state
  const [chargeDate, setChargeDate] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const error = createError || updateError || validationError;
  const isSubmitting = isCreating || isUpdating || isChecking || isUploadingFiles;

  // Initialize form when dialog opens or editingCharge changes
  useEffect(() => {
    if (isOpen) {
      if (editingCharge) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setChargeDate(editingCharge.chargeDate.split('T')[0]);
        setAmount(editingCharge.amount.toString());
        setDescription(editingCharge.description || '');
      } else {
        setChargeDate(getTodayDateString());
        setAmount('');
        setDescription('');
      }
      setValidationError('');
      setDuplicatesAcknowledged(false);
      setDuplicates([]);
      setPendingFiles([]);
    }
  }, [isOpen, editingCharge]);

  const validateForm = (): boolean => {
    if (!chargeDate) {
      setValidationError('Charge date is required');
      return false;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      setValidationError('Amount must be a valid number');
      return false;
    }

    if (amountNum === 0) {
      setValidationError('Amount cannot be zero');
      return false;
    }

    if (description && description.length > 200) {
      setValidationError('Description must be 200 characters or less');
      return false;
    }

    return true;
  };

  const performSave = async () => {
    const data = {
      isForecast,
      chargeDate: new Date(chargeDate).toISOString(),
      amount: parseFloat(amount),
      description: description.trim() || undefined,
    };

    let result;
    if (editingCharge) {
      result = await update(jobId, editingCharge.id, data);
    } else {
      result = await create(jobId, data);
    }

    if (result) {
      // Upload pending files after successful creation
      if (!editingCharge && pendingFiles.length > 0) {
        setIsUploadingFiles(true);
        try {
          await uploadChargeImages(jobId, result.id, pendingFiles);
        } catch (err) {
          console.error('Failed to upload images after charge creation:', err);
        } finally {
          setIsUploadingFiles(false);
        }
      }
      onSuccess();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!validateForm()) {
      return;
    }

    // If duplicates were already acknowledged, proceed with save
    if (duplicatesAcknowledged) {
      await performSave();
      return;
    }

    // Check for duplicates before saving
    const duplicateResult = await checkForChargeDuplicates(jobId, {
      amount: parseFloat(amount),
      description: description.trim() || undefined,
      chargeDate: new Date(chargeDate).toISOString(),
      excludeChargeId: editingCharge?.id,
    });

    if (duplicateResult.hasDuplicates) {
      setDuplicates(duplicateResult.duplicates);
      setShowDuplicateDialog(true);
      return;
    }

    // No duplicates, proceed with save
    await performSave();
  };

  const handleProceedWithDuplicates = async () => {
    setDuplicatesAcknowledged(true);
    setShowDuplicateDialog(false);
    await performSave();
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateDialog(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCharge ? 'Edit Charge' : 'Add Charge'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          <div className="space-y-2">
            <Label htmlFor="chargeDate">
              Charge Date <span className="text-red-500">*</span>
            </Label>
            <DateInput
              id="chargeDate"
              value={chargeDate || undefined}
              onChange={(iso) => setChargeDate(iso ?? '')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount <span className="text-red-500">*</span>
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Use a negative amount for credits or reductions
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              type="text"
              placeholder="e.g., Original Contract, Change Order #1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
            />
            <p className="text-xs text-gray-500">
              {description.length} / 200 characters
            </p>
          </div>

          <div className="border-t border-gray-200 my-4 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Contract Documents
            </h3>
            {editingCharge ? (
              <ChargeImagesSection
                jobId={jobId}
                chargeId={editingCharge.id}
                canDelete={true}
              />
            ) : (
              <>
                <ImageUpload
                  onUpload={async (files) => setPendingFiles((prev) => [...prev, ...files])}
                  isUploading={false}
                  compact
                  immediate
                />
                {pendingFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {pendingFiles.length} file(s) will be uploaded on save
                    </p>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="ml-2 text-muted-foreground hover:text-foreground"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-start gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : editingCharge
                  ? 'Update Charge'
                  : 'Add Charge'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Duplicate Warning Dialog */}
      <DuplicateEntryWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        entryType="Charge"
        duplicates={duplicates}
        onProceed={handleProceedWithDuplicates}
        onCancel={handleCancelDuplicates}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
}
